import { FloatingPortal, shift, useFloating, useHover, useInteractions } from '@floating-ui/react'
import ctl from '@netlify/classnames-template-literals'
import { SparklesIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { MouseEventHandler, ReactNode, useState } from 'react'
import { simpleRise } from './animation'
import { MicroLoader } from './loaders'

export interface ButtonProps {
	children?: ReactNode
	type?: 'committal' | 'neutral' | 'destructive' | 'subtle' | 'colorful' | 'green' | 'magic' | 'invisible'
	subtext?: ReactNode
	size?: 'normal' | 'short' | 'inline' | 'massive' | 'tiny'
	fullWidth?: boolean
	submit?: boolean
	onClick?: MouseEventHandler<HTMLButtonElement>
	contentKey?: string
	prefix?: 'plus' | 'refresh' | 'upload' | 'pick' | 'download' | 'sparkles' | 'back'
	prefixAlign?: 'default' | 'left'
	animatePrefix?: 'shake'
	postfix?: 'arrow' | 'revert' | 'popout'
	postfixAlign?: 'default' | 'right'
	animatePostfix?: 'shake'
	disabled?: boolean
	cursor?: 'not-allowed' | 'pointer' | 'default'
	info?: ReactNode
	tooltip?: ReactNode | null
	ariaLabel?: string

	isLoading?: boolean
}

const cursorClasses: Record<NonNullable<ButtonProps['cursor']>, string> = {
	'not-allowed': 'cursor-not-allowed',
	'pointer': 'cursor-pointer',
	'default': 'cursor-default',
}

const buttonClasses: Record<NonNullable<ButtonProps['type']>, string> = {
	committal: ctl('bg-brand-700  hover:bg-brand-600 text-white [--loader-color:var(--color-white)]'),
	neutral: ctl('bg-grey-900  hover:bg-grey-800 text-white [--loader-color:var(--color-white)]'),
	destructive: ctl('bg-red-700 hover:bg-red-600 text-white [--loader-color:var(--color-white)]'),
	// subtle: ctl('bg-grey-100 hover:bg-grey-200 text-grey-700'),
	subtle: ctl('bg-white ring ring-grey-100 hover:ring-grey-200 text-grey-700 [--loader-color:var(--color-grey-700)]'),
	colorful: ctl(`
		text-white
		bg-grey-900
		hover:bg-grey-800
		bg-gradient-to-br not-first:from-indigo-700 via-indigo-600 via-40% to-brand-600 z-10 shadow-brand-500/30
		[--loader-color:var(--color-white)]
	`),
	green: ctl(`
		text-white
		bg-grey-900
		hover:bg-grey-800
		bg-gradient-to-br from-green-400 to-green-400 z-10 shadow-green-500/50
		via-green-400/50 via-30%
		[--loader-color:var(--color-white)]
	`),
	magic: ctl(`
		text-brand-800
		bg-grey-900
		hover:bg-grey-600
		bg-gradient-to-br from-fuchsia-50 to-purple-100 z-10 shadow-brand-300/30
		via-grey-50 via-30%
		relative 
		[--loader-color:var(--color-brand-800)]
	`),
	invisible: ctl('text-grey-500 hover:text-grey-800 [--loader-color:var(--color-grey-500)]'),
}

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
	massive: ctl('px-8 py-5'),
	normal: ctl('py-3 px-5'),
	short: ctl('py-2 px-3.5'),
	inline: ctl('p-1 px-2.5'),
	tiny: ctl('p-0.5 px-2'),
}

export function Button(
	{
		children,
		type = 'neutral',
		subtext,
		size = 'normal',
		fullWidth = false,
		submit = false,
		onClick,
		contentKey,
		postfix,
		postfixAlign = 'default',
		animatePostfix,
		disabled = false,
		cursor,
		prefix,
		prefixAlign = 'default',
		animatePrefix,
		info,
		tooltip,
		ariaLabel,
		isLoading = false,
	}: ButtonProps,
) {
	const defaultClasses = ctl(`
		shadow
		group-hover/button:translate-y-[-1px] 
		group-active/button:translate-y-[2px]
		group-hover/button:shadow-md
		group-active/button:shadow-sm
		transition-all 
	`)

	const isDisabled = disabled || isLoading
	const propagateFullWidth = fullWidth && (postfixAlign === 'right' || prefixAlign === 'left')

	const className = ctl(`
			select-none
			rounded-sm font-medium
			relative overflow-hidden z-0 isolate
			 ${type !== 'invisible' ? defaultClasses : ''}
			 
			
			${fullWidth ? 'w-full' : ''}
			${buttonClasses[type]}
		`)

	// @TODO: render tooltips
	return (
		<>
			<div
				className={ctl(
					`relative 
					transition-all
					${fullWidth ? 'w-full' : 'max-w-[max-content]'} ${
						isDisabled ? 'opacity-80 saturate-70' : 'group/button'
					}`,
				)}
				data-disabled={isDisabled}
			>
				{type === 'magic'
					? (
						<>
							<div
								data-test="grad"
								className="group-hover/button:translate-y-[-1px] transition-all group-active:translate-y-[2px] absolute bottom-0 h-[20px] inset-x-[5%] blur-sm rounded-[10px] translate-y-[20] -z-10"
								style={{
									background:
										'linear-gradient(to right, var(--color-brand-500), var(--color-fuchsia-500), var(--color-red-500), var(--color-blue-500), var(--color-brand-500))',
									backgroundSize: '200% 100%',
									animation: 'rainbow-move 20s linear infinite',
								}}
							/>
							<div className="group-hover/button:translate-y-[-1px] transition-all group-active/button:translate-y-[2px] absolute inset-[0px] rounded-sm ring-1 ring-brand-500/40  -z-10" />
							<div className="group-hover/button:translate-y-[-1px] transition-all group-active/button:translate-y-[2px] absolute inset-[-1px] rounded-sm ring ring-white/40 -z-10" />
						</>
					)
					: null}

				{type === 'colorful' || type === 'green'
					? (
						<>
							<div className="group-hover/button:translate-y-[-1px] transition-all group-active/button:translate-y-[2px] absolute inset-[-1px] rounded-sm ring ring-white/40 -z-10" />
						</>
					)
					: null}

				<motion.button
					type={submit ? 'submit' : 'button'}
					className={ctl(
						`${className} ${
							cursor ? cursorClasses[cursor] : (isDisabled ? 'cursor-not-allowed' : 'cursor-pointer')
						}`,
					)}
					onClick={onClick}
					disabled={isDisabled}
					aria-label={ariaLabel}
				>
					<div
						className={ctl(
							`flex items-center justify-center relative z-10 ${propagateFullWidth ? 'w-full' : ''}`,
						)}
					>
						<div
							className={ctl(
								`${sizeClasses[size]} relative ${propagateFullWidth ? 'w-full' : ''}`,
							)}
						>
							{type === 'magic'
								? (
									<>
										<div className="absolute inset-x-0 top-0 h-[50%] rounded-sm bg-white/10" />
									</>
								)
								: null}
							{isLoading
								? (
									<div className="absolute inset-0 flex items-center justify-center">
										<MicroLoader color={null} />
									</div>
								)
								: null}
							<AnimatePresence mode="popLayout" initial={false}>
								<motion.div
									transition={{
										type: 'spring',
										duration: 1,
										bounce: 0,
										filter: { bounce: 0 },
										clipPath: { bounce: 0 },
									}}
									initial={{ opacity: 0, clipPath: 'inset(0 100% 0 0)', filter: 'blur(3px)' }}
									animate={{
										opacity: isLoading ? 0 : 1,
										clipPath: 'inset(0 0 0 0)',
										filter: 'blur(0px)',
									}}
									exit={{ opacity: 0, clipPath: 'inset(0 0 0 100%)', filter: 'blur(3px)' }}
									key={contentKey}
									className={propagateFullWidth ? 'w-full' : ''}
								>
									<div
										className={ctl(
											`flex items-center ${propagateFullWidth ? 'w-full' : ''}`,
										)}
									>
										{prefix
											? (
												<div
													className={ctl(`
														${
														fullWidth && prefixAlign === 'left'
															? 'mr-auto pr-4'
															: 'mr-2'
													}
														${
														animatePrefix === 'shake'
															? 'ml-2 group-hover/button:animate-[arrow-nudge-left_0.7s_ease-in-out_infinite]'
															: ''
													}
													`)}
												>
													{prefix === 'plus'
														? <PlusPrefix />
														: prefix === 'refresh'
														? <RefreshPrefix />
														: prefix === 'upload'
														? <UploadPrefix />
														: prefix === 'pick'
														? <PickPrefix />
														: prefix === 'download'
														? <DownloadPrefix />
														: prefix === 'sparkles'
														? <SparklesIcon className="size-3" />
														: prefix === 'back'
														? (
															<div className="rotate-180">
																<ArrowPostfix />
															</div>
														)
														: null}
												</div>
											)
											: null}
										{children}
										{info
											? (
												<div className="ml-2 opacity-50">
													<InfoWithPopup tooltip={info} />
												</div>
											)
											: null}
										{postfix
											? (
												<div
													className={ctl(`
														${
														fullWidth && postfixAlign === 'right'
															? 'ml-auto mr-2 pl-4'
															: 'ml-2'
													}
														${
														animatePostfix === 'shake'
															? 'group-hover/button:animate-[arrow-nudge_0.7s_ease-in-out_infinite]'
															: ''
													}
													`)}
												>
													{postfix === 'arrow'
														? <ArrowPostfix />
														: postfix === 'popout'
														? <PopoutPostfix />
														: <RevertPostfix />}
												</div>
											)
											: null}
									</div>
									{subtext ? <div className="text-sm text-brand-100">{subtext}</div> : null}
								</motion.div>
							</AnimatePresence>
						</div>
					</div>
				</motion.button>
			</div>
		</>
	)
}

function InfoWithPopup({ tooltip }: { tooltip: ReactNode }) {
	const [isOpen, setIsOpen] = useState(false)

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'bottom',
		middleware: [shift()],
	})

	const hover = useHover(context, {
		restMs: 150,
	})

	const { getReferenceProps, getFloatingProps } = useInteractions([hover])

	return (
		<>
			<div {...getReferenceProps()} ref={refs.setReference}>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					fill="none"
					viewBox="0 0 16 16"
					id="Information-Circle--Streamline-Nova"
					height={16}
					width={16}
				>
					<path
						fill="currentColor"
						fillRule="evenodd"
						d="M1.3333333333333333 8C1.3333333333333333 4.318099999999999 4.318099999999999 1.3333333333333333 8 1.3333333333333333c3.6818666666666666 0 6.666666666666666 2.9847666666666663 6.666666666666666 6.666666666666666 0 3.6818666666666666 -2.9848 6.666666666666666 -6.666666666666666 6.666666666666666 -3.6818999999999997 0 -6.666666666666666 -2.9848 -6.666666666666666 -6.666666666666666ZM8 0C3.58172 0 0 3.58172 0 8c0 4.418266666666666 3.58172 8 8 8 4.418266666666666 0 8 -3.581733333333333 8 -8 0 -4.418279999999999 -3.581733333333333 -8 -8 -8Zm0.6666666666666666 3.333333333333333v1.3333333333333333h-1.3333333333333333V3.333333333333333h1.3333333333333333Zm-1.3333333333333333 4H6V6h2.6666666666666665v4.666666666666666h1.3333333333333333v1.3333333333333333H6v-1.3333333333333333h1.3333333333333333v-3.333333333333333Z"
						clipRule="evenodd"
						strokeWidth={0.6667}
					/>
				</svg>
			</div>

			<AnimatePresence>
				{isOpen
					? (
						<FloatingPortal>
							<div
								{...getFloatingProps()}
								ref={refs.setFloating}
								style={floatingStyles}
								className="pointer-events-none"
							>
								<motion.div
									{...simpleRise()}
									className="[contain:layout] text-xs bg-white/80 ring ring-grey-100 text-grey-700 text-center rounded-sm p-2 shadow max-w-80"
								>
									{tooltip}
								</motion.div>
							</div>
						</FloatingPortal>
					)
					: null}
			</AnimatePresence>
		</>
	)
}

function RefreshPrefix() {
	return (
		<>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				fill="none"
				viewBox="0 0 16 16"
				id="Synchronize-Arrows-1--Streamline-Nova"
				height={16}
				width={16}
			>
				<path
					fill="currentColor"
					fillRule="evenodd"
					d="M2.431693333333333 4.333333333333333C3.6247 2.5251466666666667 5.673946666666667 1.3333333333333333 8.000533333333333 1.3333333333333333c3.6818666666666666 0 6.666666666666666 2.9847666666666663 6.666666666666666 6.666666666666666h1.3333333333333333c0 -4.418279999999999 -3.581733333333333 -8 -8 -8C5.216813333333333 0 2.76624 1.4217199999999999 1.33358 3.5768866666666668V1H0.00024986266666666664v4c0 0.36818666666666666 0.298476804 0.6666666666666666 0.6666634706666666 0.6666666666666666h4v-1.3333333333333333H2.431693333333333ZM14.666933333333333 12.423133333333332V15h1.3333333333333333v-4c0 -0.36819999999999997 -0.29846666666666666 -0.6666666666666666 -0.6666666666666666 -0.6666666666666666l-4 0v1.3333333333333333h2.2352C12.3758 13.474866666666665 10.326533333333334 14.666666666666666 8 14.666666666666666 4.318099999999999 14.666666666666666 1.3333333333333333 11.681866666666666 1.3333333333333333 8H0c0 4.418266666666666 3.58172 8 8 8 2.783666666666667 0 5.234266666666667 -1.4217333333333333 6.666933333333334 -3.5768666666666666Z"
					clipRule="evenodd"
					strokeWidth={0.6667}
				/>
			</svg>
		</>
	)
}

function PlusPrefix() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 16 16"
			id="Add-Thin--Streamline-Nova"
			height={16}
			width={16}
		>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="M7.333333333333333 8.666666666666666v6.666666666666666h1.3333333333333333V8.666666666666666h6.666666666666666v-1.3333333333333333H8.666666666666666V0.6666666666666666h-1.3333333333333333v6.666666666666666H0.6666666666666666v1.3333333333333333h6.666666666666666Z"
				clipRule="evenodd"
				strokeWidth={0.6667}
			/>
		</svg>
	)
}

function ArrowPostfix() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			id="Keyboard-Right-Arrow--Streamline-Nova"
			height={16}
			width={16}
		>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="m15.7823 3.16113 7.7782 7.77817c0.5858 0.5858 0.5858 1.5355 0 2.1213l-7.7782 7.7782 -2.1213 -2.1213L18.8785 13.5 0 13.5v-3l18.8785 0 -5.2175 -5.21755 2.1213 -2.12132Z"
				clipRule="evenodd"
				strokeWidth={1}
			/>
		</svg>
	)
}

function UploadPrefix() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 16 16"
			id="Data-Transfer-Harddrive-Upload_1--Streamline-Nova"
			height={16}
			width={16}
		>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="M7.528599999999999 0.195262c0.2603333333333333 -0.26034939999999995 0.6824666666666667 -0.26034939999999995 0.9427999999999999 0l2.6666666666666665 2.6666646666666667 -0.9427999999999999 0.9428133333333333L8.666666666666666 2.27614V8h-1.3333333333333333V2.27614l-1.5285933333333332 1.5286 -0.9428133333333333 -0.9428133333333333L7.528599999999999 0.195262ZM3.1626866666666666 4l-1.5999999999999999 5.333333333333333H14.437333333333331l-1.5999999999999999 -5.333333333333333H12V2.6666666666666665h1.3333333333333333c0.2944 0 0.5539333333333333 0.1931133333333333 0.6385333333333333 0.47509999999999997l1.9955999999999998 6.651833333333333c0.02113333333333333 0.065 0.03253333333333333 0.13433333333333333 0.03253333333333333 0.20639999999999997v5.333333333333333c0 0.36819999999999997 -0.29846666666666666 0.6666666666666666 -0.6666666666666666 0.6666666666666666H0.6666666666666666c-0.36819 0 -0.6666666666666666 -0.29846666666666666 -0.6666666666666666 -0.6666666666666666v-5.333333333333333c0 -0.07206666666666667 0.011427733333333332 -0.1414 0.03256933333333333 -0.20639999999999997L2.028113333333333 3.1417666666666664C2.112713333333333 2.8597799999999998 2.37226 2.6666666666666665 2.6666666666666665 2.6666666666666665h1.3333333333333333v1.3333333333333333H3.1626866666666666ZM1.3333333333333333 14.666666666666666v-4h13.333333333333332v4H1.3333333333333333Zm11.333333333333332 -1.3333333333333333c0.36819999999999997 0 0.6666666666666666 -0.29846666666666666 0.6666666666666666 -0.6666666666666666s-0.29846666666666666 -0.6666666666666666 -0.6666666666666666 -0.6666666666666666 -0.6666666666666666 0.29846666666666666 -0.6666666666666666 0.6666666666666666 0.29846666666666666 0.6666666666666666 0.6666666666666666 0.6666666666666666Zm-2 -0.6666666666666666c0 0.36819999999999997 -0.29846666666666666 0.6666666666666666 -0.6666666666666666 0.6666666666666666s-0.6666666666666666 -0.29846666666666666 -0.6666666666666666 -0.6666666666666666 0.29846666666666666 -0.6666666666666666 0.6666666666666666 -0.6666666666666666 0.6666666666666666 0.29846666666666666 0.6666666666666666 0.6666666666666666Z"
				clipRule="evenodd"
				strokeWidth={0.6667}
			/>
		</svg>
	)
}

function PopoutPostfix() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 16 16"
			id="Maximize-Square-4--Streamline-Nova"
			height={16}
			width={16}
		>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="M13.723866666666666 1.3333333333333333H10.666666666666666V0h4.666666666666666c0.36819999999999997 0 0.6666666666666666 0.2984766666666666 0.6666666666666666 0.6666666666666666v4.666666666666666h-1.3333333333333333V2.27614l-5.528599999999999 5.528593333333333 -0.9427999999999999 -0.9427999999999999L13.723866666666666 1.3333333333333333ZM0 4c0 -0.36818666666666666 0.2984766666666666 -0.6666666666666666 0.6666666666666666 -0.6666666666666666h6.666666666666666v1.3333333333333333H1.3333333333333333v10h10v-6h1.3333333333333333v6.666666666666666c0 0.36819999999999997 -0.29846666666666666 0.6666666666666666 -0.6666666666666666 0.6666666666666666H0.6666666666666666c-0.36819 0 -0.6666666666666666 -0.29846666666666666 -0.6666666666666666 -0.6666666666666666V4Z"
				clipRule="evenodd"
				strokeWidth={0.6667}
			/>
		</svg>
	)
}

function PickPrefix() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 16 16"
			id="Camera-Polaroid-Instant-Camera-Album--Streamline-Nova"
			height={16}
			width={16}
		>
			<desc>{'\n    Camera Polaroid Instant Camera Album Streamline Icon: https://streamlinehq.com\n  '}</desc>
			<path
				stroke="currentColor"
				strokeLinejoin="round"
				strokeWidth={1.3333}
				d="M15.333333333333332 0.6666666666666666H0.6666666666666666v14.666666666666666h14.666666666666666V0.6666666666666666Z"
			/>
			<path
				stroke="currentColor"
				strokeLinejoin="round"
				strokeWidth={1.3333}
				d="M6.666666666666666 3.333333333333333H3.333333333333333v3.333333333333333h3.333333333333333V3.333333333333333Z"
			/>
			<path
				stroke="currentColor"
				strokeLinejoin="round"
				strokeWidth={1.3333}
				d="M12.666666666666666 3.333333333333333h-3.333333333333333v3.333333333333333h3.333333333333333V3.333333333333333Z"
			/>
			<path
				stroke="currentColor"
				strokeLinejoin="round"
				strokeWidth={1.3333}
				d="M6.666666666666666 9.333333333333332H3.333333333333333v3.333333333333333h3.333333333333333v-3.333333333333333Z"
			/>
		</svg>
	)
}

function DownloadPrefix() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={1.5}
			height={16}
			width={16}
			stroke="currentColor"
		>
			<path
				strokeLinecap="round"
				strokeLinejoin="round"
				d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
			/>
		</svg>
	)
}

function RevertPostfix() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 16 16"
			id="Keyboard-Enter-Return-Curve--Streamline-Nova"
			height={16}
			width={16}
		>
			<desc>Keyboard Enter Return Curve Streamline Icon: https://streamlinehq.com</desc>
			<path
				fill="currentColor"
				fillRule="evenodd"
				d="M8.666666666666666 2.6666666666666665h2.5C13.099666666666666 2.6666666666666665 14.666666666666666 4.233666666666666 14.666666666666666 6.166666666666666s-1.5669999999999997 3.5 -3.5 3.5H2.27614l2.8619333333333334 -2.8619333333333334 -0.9428133333333333 -0.9428066666666666L0.195262 9.861933333333333c-0.26034939999999995 0.2603333333333333 -0.26034939999999995 0.6824666666666667 0 0.9427999999999999l3.9999979999999997 4 0.9428133333333333 -0.9427999999999999L2.27614 11H11.166666666666666c2.6694 0 4.833333333333333 -2.163933333333333 4.833333333333333 -4.833333333333333C16 3.4972933333333334 13.836066666666667 1.3333333333333333 11.166666666666666 1.3333333333333333H8.666666666666666v1.3333333333333333Z"
				clipRule="evenodd"
				strokeWidth={0.6667}
			/>
		</svg>
	)
}
